import { RubyLoader } from '../components/RubyLoader';
import { RubyGemMark } from '../components/RubyGemMark';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tenant } from '../types';

// Deal interface — the deals a Send Deal invite can carry.
//
// Read from the ADMIN deal endpoints only (`GET /admin/deals`). The public ones
// deliberately no longer return `packageDiscounts` or `packagesTotals`: they
// were exposing per-package discount values and the free-text negotiation
// `reason` behind them on unauthenticated URLs, and a `reason` must never reach
// a customer-visible surface.
interface Deal {
  id: string;
  code: string;
  name: string;
  stripePriceId: string;
  monthlyAmount: number;
  trialDays: number;
  packageIds?: string[];
  productId?: string;
  isActive: string;
  isDefaultDeal: boolean;
  maxUses?: number;
  usedCount: number;
  createdAt: number;
  /** The deal's own monthly price in integer cents — authoritative at signup (§12/A2). */
  priceCents?: number;
  /** packageId -> discount, in the SAME shape a PlanAssignment stores. ADMIN-ONLY. */
  packageDiscounts?: Record<string, PlanDiscount>;
  /** Device SKUs offered with the deal. The API keeps both names in lockstep. */
  deviceSkuIds?: string[];
  devicePackageIds?: string[];
}
import { api } from '../services/apiService';
import { loadingService } from '../services/loadingService';
import { applyTheme } from '../services/theme';
import SendDealModal from '../components/commandCenter/SendDealModal';
import InvitesView from '../components/commandCenter/InvitesView';
import BillingDriftPanel from '../components/finance/BillingDriftPanel';
import { type PlanDiscount } from '../services/billingApi';
import { Toast } from '../components/Toast';
import {
  Search,
  Building,
  Building2,
  CheckCircle,
  RefreshCw,
  X,
  Copy,
  Check,
  AlertCircle,
  TrendingUp,
  Users,
  DollarSign,
  ChevronRight,
  ArrowRight,
  Filter,
  Inbox,
  UserPlus,
  Mail,
  Phone,
  Calendar,
  MoreVertical,
  Eye,
  Edit2,
  Trash2,
  Settings,
  Zap,
  Clock,
  Activity,
  Target,
  Layers,
  Grid,
  List as ListIcon,
  LayoutDashboard,
  Sparkles,
  MapPin,
  Monitor,
  AlertTriangle,
  FileText,
  Send,
} from 'lucide-react';
import { useRevenueSummary, revenueMoney } from '../hooks/useRevenueSummary';
import type { RevenueSummary } from '../services/billingApi';

// Types
type ViewMode = 'dashboard' | 'leads' | 'invites' | 'tenants';
type LeadStatus = 'new' | 'contacted' | 'onboarded' | 'closed';

// Format status for display (standalone function for use in sub-components)
const formatStatus = (status: string) => {
  switch (status.toLowerCase()) {
    case 'payment_unlinked': return 'Pending Payment';
    case 'past_due': return 'Past Due';
    case 'trialing': return 'Trial';
    default: return status.charAt(0).toUpperCase() + status.slice(1);
  }
};

// Local Lead interface matching backend shape
interface Lead {
  id?: string;
  businessName?: string;
  email?: string;
  name?: string;
  contactName?: string;
  phone?: string;
  message?: string;
  businessType?: string;
  status?: LeadStatus;
  onboardedTenantId?: string;
  submittedAt?: number;
  createdAt?: number;
  updatedAt?: number;
}

interface QuickMetric {
  label: string;
  value: number | string;
  subtext?: string;
  trend?: 'up' | 'down' | 'neutral';
  /** The figure is still being read -- draw the gem, not the em dash. */
  loading?: boolean;
  color: 'amber' | 'blue' | 'green' | 'purple' | 'red';
  icon: React.ReactNode;
}

const SuperAdminCommandCenter: React.FC = () => {
  const navigate = useNavigate();

  // View state
  const [activeView, setActiveView] = useState<ViewMode>('dashboard');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  
  // Separate search terms for leads and tenants
  const [leadsSearchTerm, setLeadsSearchTerm] = useState('');
  const [tenantsSearchTerm, setTenantsSearchTerm] = useState('');
  
  // Pagination state
  const ITEMS_PER_PAGE = 10;
  const [leadsPage, setLeadsPage] = useState(1);
  const [tenantsPage, setTenantsPage] = useState(1);
  
  // Data states
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  /** Every money figure on this page. Never computed here — see the hook. */
  const revenue = useRevenueSummary();

  // Bottom sheet states
  // Lead detail opens as a small centred popup. There is no bottom drawer.
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);

  // Send Deal popup — replaces the old "New Tenant" drawer
  const [sendDealOpen, setSendDealOpen] = useState(false);
  const [sendDealLead, setSendDealLead] = useState<Lead | null>(null);
  /** Bumped after a send so the Invites view refetches. */
  const [invitesVersion, setInvitesVersion] = useState(0);

  // Toast
  const [toast, setToast] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);

  // Lead filtering
  const [statusFilter, setStatusFilter] = useState<LeadStatus | 'all'>('all');

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    loadingService.start();
    try {
      const [tenantList, leadList, dealsRes] = await Promise.all([
        api.tenants.getAll(),
        api.leads.getAll(),
        api.deals.list('true').catch(() => ({ deals: [] })),
      ]);
      setTenants(Array.isArray(tenantList) ? tenantList : []);

      // Normalize leads
      const normalized = (Array.isArray(leadList) ? leadList : []).map((lead: any) => ({
        ...lead,
        status: normalizeLeadStatus(lead.status),
        submittedAt: lead.submittedAt || lead.createdAt || lead.updatedAt || Date.now(),
      }));
      setLeads(normalized.sort((a: any, b: any) => (b.submittedAt || 0) - (a.submittedAt || 0)));

      const fetchedDeals = Array.isArray(dealsRes?.deals) ? dealsRes.deals : [];
      setDeals(fetchedDeals);
    } catch (err) {
      console.error('Failed to load data', err);
      showToast('Failed to load data', 'error');
    } finally {
      setLoading(false);
      loadingService.stop();
    }
  }, []);

  useEffect(() => {
    loadData();
    applyTheme('dark');
  }, []);

  // Helpers
  const normalizeLeadStatus = (status?: string): LeadStatus => {
    const lowered = (status || '').toLowerCase();
    const allowed: LeadStatus[] = ['new', 'contacted', 'onboarded', 'closed'];
    return allowed.includes(lowered as LeadStatus) ? (lowered as LeadStatus) : 'new';
  };

  const showToast = (message: string, type: 'success' | 'error' | 'info') => {
    setToast({ message, type });
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    // The revenue summary is refreshed WITH the rest. Leaving it behind would
    // put a fresh tenant list beside a stale MRR, which reads as the two
    // disagreeing rather than as one of them being old.
    await Promise.all([loadData(), revenue.reload()]);
    setRefreshing(false);
    showToast('Data refreshed', 'success');
  };

  // Metrics
  //
  // NOTHING here adds up money. Every figure comes from billing-service's
  // revenue summary, which reads all of Stripe's recurring items and all of the
  // active plan assignments. The previous version summed `tenant.subscriptionCost`
  // — the FIRST Stripe line item on the first subscription — so it reported one
  // location's package price as the platform's MRR and never saw a device at
  // all. See `useRevenueSummary`.
  const metrics = useMemo((): QuickMetric[] => {
    const rev = revenue.summary;
    const totalTenants = rev?.counts.tenants ?? tenants.length;
    const activeTenants = rev?.counts.activeTenants
      ?? tenants.filter(t => (t.subscriptionStatus || '').toLowerCase() === 'active').length;
    const newLeads = leads.filter(l => l.status === 'new').length;
    const contactedLeads = leads.filter(l => l.status === 'contacted').length;

    // An unknown figure shows as an em dash. It must never fall back to a
    // locally computed total: a wrong number that looks authoritative is the
    // whole reason this panel was rebuilt.
    const billedTotal = rev ? revenueMoney(rev.billed.totalCents) : '—';
    const contractedTotal = rev ? revenueMoney(rev.contracted.totalCents) : '—';
    const deltaCents = rev ? rev.billed.softwareCents + rev.billed.otherCents - rev.contracted.totalCents : 0;
    const contractedSubtext = !rev
      ? 'software only'
      : Math.abs(deltaCents) <= 1
        ? 'matches billed · software only'
        : `${deltaCents > 0 ? '+' : '−'}${revenueMoney(Math.abs(deltaCents))} vs billed`;

    return [
      {
        label: 'Total Tenants',
        value: totalTenants,
        subtext: `${activeTenants} active`,
        color: 'amber',
        icon: <Building size={20} />,
      },
      {
        label: 'New Leads',
        value: newLeads,
        subtext: `${contactedLeads} contacted`,
        color: 'blue',
        icon: <AlertCircle size={20} />,
      },
      {
        label: 'MRR — Billed',
        value: billedTotal,
        loading: revenue.loading && !rev,
        subtext: rev
          ? `software ${revenueMoney(rev.billed.softwareCents)} · devices ${revenueMoney(rev.billed.deviceCents)}`
          : 'from Stripe',
        color: 'green',
        icon: <DollarSign size={20} />,
      },
      {
        label: 'Contracted',
        value: contractedTotal,
        loading: revenue.loading && !rev,
        subtext: contractedSubtext,
        color: Math.abs(deltaCents) > 1 ? 'red' : 'purple',
        icon: <FileText size={20} />,
      },
    ];
  }, [tenants, leads, revenue.summary, revenue.loading]);

  // Filtered data with pagination
  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      const searchLower = leadsSearchTerm.toLowerCase();
      const matchesSearch = !leadsSearchTerm ||
        lead.businessName?.toLowerCase().includes(searchLower) ||
        lead.email?.toLowerCase().includes(searchLower) ||
        lead.name?.toLowerCase().includes(searchLower) ||
        lead.phone?.toLowerCase().includes(searchLower);
      const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [leads, leadsSearchTerm, statusFilter]);

  const filteredTenants = useMemo(() => {
    return tenants.filter(tenant => {
      const searchLower = tenantsSearchTerm.toLowerCase();
      return !tenantsSearchTerm || (
        tenant.name?.toLowerCase().includes(searchLower) ||
        tenant.businessName?.toLowerCase().includes(searchLower) ||
        tenant.adminEmail?.toLowerCase().includes(searchLower) ||
        tenant.adminName?.toLowerCase().includes(searchLower) ||
        tenant.id?.toLowerCase().includes(searchLower)
      );
    });
  }, [tenants, tenantsSearchTerm]);

  // Paginated data
  const paginatedLeads = useMemo(() => {
    const startIdx = (leadsPage - 1) * ITEMS_PER_PAGE;
    return filteredLeads.slice(startIdx, startIdx + ITEMS_PER_PAGE);
  }, [filteredLeads, leadsPage]);

  const paginatedTenants = useMemo(() => {
    const startIdx = (tenantsPage - 1) * ITEMS_PER_PAGE;
    return filteredTenants.slice(startIdx, startIdx + ITEMS_PER_PAGE);
  }, [filteredTenants, tenantsPage]);

  const totalLeadsPages = Math.ceil(filteredLeads.length / ITEMS_PER_PAGE);
  const totalTenantsPages = Math.ceil(filteredTenants.length / ITEMS_PER_PAGE);

  // Reset page when search/filter changes
  useEffect(() => {
    setLeadsPage(1);
  }, [leadsSearchTerm, statusFilter]);

  useEffect(() => {
    setTenantsPage(1);
  }, [tenantsSearchTerm]);

  // Lead status counts
  const leadStatusCounts = useMemo(() => {
    return {
      all: leads.length,
      new: leads.filter(l => l.status === 'new').length,
      contacted: leads.filter(l => l.status === 'contacted').length,
      onboarded: leads.filter(l => l.status === 'onboarded').length,
      closed: leads.filter(l => l.status === 'closed').length,
    };
  }, [leads]);

  // Panel handlers
  const openLeadDetail = (lead: Lead) => setSelectedLead(lead);

  // A tenant row goes straight to the tenant's own profile page.
  const openTenantDetail = (tenant: Tenant) => navigate(`/tenant/${tenant.id}`);

  /**
   * "Add Tenant" and "Convert" both send a deal now. Nothing is created here:
   * the prospect's own signup through the offer link creates the tenant.
   */
  const openSendDeal = (lead?: Lead) => {
    setSelectedLead(null);
    setSendDealLead(lead ?? null);
    setSendDealOpen(true);
  };

  const closeLeadDetail = () => setSelectedLead(null);

  // Lead actions
  const updateLeadStatus = async (id: string, status: LeadStatus) => {
    try {
      const lead = leads.find(l => l.id === id);
      if (lead) {
        await api.leads.update(id, { ...lead, status });
        await loadData();
        showToast(`Lead marked as ${status}`, 'success');
      }
    } catch (err) {
      showToast('Failed to update lead status', 'error');
    }
  };

  const deleteLead = async (id: string) => {
    if (!confirm('Are you sure you want to delete this lead?')) return;
    try {
      await api.leads.delete(id);
      await loadData();
      showToast('Lead deleted', 'success');
      closeLeadDetail();
    } catch (err) {
      showToast('Failed to delete lead', 'error');
    }
  };

  const formatDate = (timestamp?: number) => {
    if (!timestamp) return '—';
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Status badge colors
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'contacted': return 'bg-yellow-100 text-yellow-800';
      case 'onboarded': return 'bg-emerald-100 text-emerald-800';
      case 'closed': return 'bg-gray-200 text-gray-700';
      case 'active': return 'bg-emerald-500 text-white';
      case 'trialing':
      case 'trial': return 'bg-rose-500 text-white';
      case 'payment_unlinked': return 'bg-orange-500 text-white';
      case 'suspended': return 'bg-red-500 text-white';
      case 'inactive': return 'bg-gray-400 text-white';
      case 'past_due': return 'bg-red-400 text-white';
      case 'canceled':
      case 'cancelled': return 'bg-gray-500 text-white';
      default: return 'bg-gray-300 text-gray-800';
    }
  };

  // View tabs
  const viewTabs = [
    { id: 'dashboard', label: 'Overview', icon: LayoutDashboard },
    { id: 'leads', label: 'Leads', icon: Target, count: leadStatusCounts.new },
    { id: 'invites', label: 'Invites', icon: Send },
    { id: 'tenants', label: 'Tenants', icon: Building2, count: tenants.length },
  ];

  return (
    <div className="min-h-screen -m-3 md:-m-6">
      {/* Top Control Banner - Full width (coffee-900 style like MenuHub) */}
      <div className="bg-coffee-900 text-white px-6 py-5 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-4">
              {/* Command Center Icon */}
              <div className="w-12 h-12 rounded-xl bg-coffee-800 flex items-center justify-center relative">
                <Zap size={22} className="text-[#DC143C]" />
                {/* Badge in corner */}
                <div className="absolute -bottom-1.5 -right-1.5 w-6 h-6 rounded-full bg-coffee-700 flex items-center justify-center">
                  <Activity size={14} className="text-white" />
                </div>
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Command Center</h1>
                <p className="text-white/50 text-xs mt-1">Manage leads, tenants & operations</p>
              </div>
            </div>
            
            {/* View Tabs */}
            <div className="hidden md:flex items-center bg-coffee-800/50 rounded-xl p-1 ml-4">
              {viewTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveView(tab.id as ViewMode)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    activeView === tab.id
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-white/70 hover:text-white hover:bg-coffee-700/50'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                  {tab.count !== undefined && tab.count > 0 && (
                    <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${
                      activeView === tab.id
                        ? 'bg-[#DC143C] text-white'
                        : 'bg-white/20 text-white'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
            
            {/* Quick Stats */}
            <div className="hidden lg:flex items-center gap-5 ml-8 pl-8 border-l border-white/10">
              <div className="text-center">
                <p className="text-2xl font-bold text-white">{loading ? <RubyGemMark size={24} drawing title="Loading" /> : tenants.length}</p>
                <p className="text-[10px] text-white/40 uppercase tracking-wider">Tenants</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-400">{loading ? <RubyGemMark size={24} drawing title="Loading" /> : leads.filter(l => l.status === 'new').length}</p>
                <p className="text-[10px] text-white/40 uppercase tracking-wider">New Leads</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-400">{loading ? <RubyGemMark size={24} drawing title="Loading" /> : tenants.filter(t => t.subscriptionStatus === 'active').length}</p>
                <p className="text-[10px] text-white/40 uppercase tracking-wider">Active</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Send Deal — the only way a new tenant starts now */}
            <button
              onClick={() => openSendDeal()}
              className="flex items-center gap-2 px-5 py-2.5 bg-black text-white rounded-xl font-semibold text-sm hover:bg-gray-900 transition-colors shadow-sm"
            >
              <Send size={17} />
              <span>Send Deal</span>
            </button>

            {/* Refresh - Icon only */}
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2.5 bg-coffee-800 text-white rounded-xl hover:bg-coffee-700 transition-colors border border-white/10"
              title="Refresh data"
            >
              {refreshing ? <RubyGemMark size={18} drawing color="currentColor" /> : <RefreshCw size={18} />}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area - White background */}
      <div className="p-4 md:p-6 bg-white min-h-[calc(100vh-100px)]">
        {loading ? <RubyLoader variant="page" label="Loading the command center…" /> : (
          <>
            {/* Dashboard View */}
            {activeView === 'dashboard' && (
              <DashboardView
                metrics={metrics}
                revenue={revenue.summary}
                revenueError={revenue.error}
                revenueLoading={revenue.loading}
                leads={leads.filter(l => l.status === 'new').slice(0, 5)}
                tenants={tenants.filter(t => t.subscriptionStatus === 'active').slice(0, 5)}
                onLeadClick={openLeadDetail}
                onTenantClick={openTenantDetail}
                onViewAllLeads={() => setActiveView('leads')}
                onViewAllTenants={() => setActiveView('tenants')}
                formatDate={formatDate}
                getStatusColor={getStatusColor}
              />
            )}

            {/* Leads View */}
            {activeView === 'leads' && (
              <LeadsView
                leads={paginatedLeads}
                allLeadsCount={filteredLeads.length}
                searchTerm={leadsSearchTerm}
                setSearchTerm={setLeadsSearchTerm}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                statusCounts={leadStatusCounts}
                onLeadClick={openLeadDetail}
                onOnboard={openSendDeal}
                formatDate={formatDate}
                getStatusColor={getStatusColor}
                currentPage={leadsPage}
                totalPages={totalLeadsPages}
                onPageChange={setLeadsPage}
              />
            )}

            {/* Invites View — every deal sent, and how far each prospect got */}
            {activeView === 'invites' && (
              <InvitesView reloadKey={invitesVersion} onSendDeal={() => openSendDeal()} onToast={showToast} />
            )}

            {/* Tenants View */}
            {activeView === 'tenants' && (
              <TenantsView
                tenants={paginatedTenants}
                allTenantsCount={filteredTenants.length}
                searchTerm={tenantsSearchTerm}
                setSearchTerm={setTenantsSearchTerm}
                viewMode={viewMode}
                setViewMode={setViewMode}
                onTenantClick={openTenantDetail}
                onNavigateToProfile={(id) => navigate(`/tenant/${id}`)}
                formatDate={formatDate}
                getStatusColor={getStatusColor}
                currentPage={tenantsPage}
                totalPages={totalTenantsPages}
                onPageChange={setTenantsPage}
              />
            )}
          </>
        )}
      </div>

      <SendDealModal
        open={sendDealOpen}
        deals={deals}
        lead={sendDealLead}
        onClose={() => setSendDealOpen(false)}
        onSent={async (invite) => {
          setSendDealOpen(false);
          setInvitesVersion((v) => v + 1);
          showToast(`Offer sent to ${invite.contactName} at ${invite.businessName}`, 'success');
          // A lead that has been sent a deal has been contacted.
          if (sendDealLead?.id && sendDealLead.status === 'new') {
            await api.leads.update(sendDealLead.id, { ...sendDealLead, status: 'contacted' }).catch(() => undefined);
            await loadData().catch(() => undefined);
          }
        }}
      />

      {/* Lead detail — a small centred popup (the bottom drawer is gone) */}
      {selectedLead && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-stone-950/40 p-4 backdrop-blur-[2px]"
          onMouseDown={(e) => { if (e.target === e.currentTarget) closeLeadDetail(); }}
        >
          <div role="dialog" aria-modal="true" aria-label="Lead details" className="relative max-h-[calc(100vh-32px)] w-full max-w-[560px] overflow-y-auto rounded-[22px] bg-white px-6 pb-6 pt-16 shadow-[0_40px_90px_-30px_rgba(10,10,11,0.55)]">
            <button
              type="button"
              onClick={closeLeadDetail}
              aria-label="Close"
              className="absolute right-4 top-4 grid h-11 w-11 place-items-center rounded-xl border border-stone-200 text-stone-500 hover:bg-stone-50"
            >
              <X size={18} />
            </button>
            <LeadDetailPanel
              lead={selectedLead}
              onUpdateStatus={updateLeadStatus}
              onDelete={deleteLead}
              onOnboard={() => openSendDeal(selectedLead)}
              formatDate={formatDate}
              getStatusColor={getStatusColor}
            />
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

// ============================================
// Sub-components
// ============================================

// Dashboard Overview
/**
 * What ONE tenant actually pays each month.
 *
 * Reads the summary's per-tenant row rather than `tenant.subscriptionCost`:
 * that field is the first line item of the first subscription, so a tenant with
 * two locations and a rented terminal showed a fraction of its real bill.
 *
 * The dot marks a tenant whose contracted plan and billed amount disagree —
 * a package that never reached Stripe, or a Stripe line nothing sold. Devices
 * are excluded from that comparison; they have no contracted counterpart.
 */
const TenantRevenueCell: React.FC<{ tenant: Tenant; revenue: RevenueSummary | null }> = ({ tenant, revenue }) => {
  const row = revenue?.tenants.find((t) => t.tenantId === tenant.id);
  if (!row) {
    // No summary yet, or a tenant Stripe knows nothing about. Deliberately not
    // falling back to subscriptionCost — a plausible wrong number is worse here
    // than an honest blank.
    return <span className="text-sm text-gray-400">{revenue ? '—' : '···'}</span>;
  }
  const parts = [
    row.locations ? `${row.locations} loc` : '',
    row.deviceSubscriptions ? `${row.deviceSubscriptions} dev` : '',
  ].filter(Boolean).join(' · ');
  return (
    <span className="flex items-center gap-2">
      {row.divergent && (
        <span
          className="w-2 h-2 rounded-full bg-amber-500 shrink-0"
          title={row.deltaCents != null
            ? `Billed ${revenueMoney(row.billedSoftwareCents)} of software but contracted ${revenueMoney(row.contractedCents)}`
            : 'Contracted amount could not be read'}
        />
      )}
      <span className="text-right">
        <span className="block text-sm font-medium text-green-600">{revenueMoney(row.billedCents)}/mo</span>
        {parts && <span className="block text-[11px] text-gray-400">{parts}</span>}
      </span>
    </span>
  );
};

/** A compact counter beside the money cards. Amber only when it is genuinely actionable. */
const CoverageTile: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  subtext: string;
  alert?: boolean;
  loading?: boolean;
}> = ({ icon, label, value, subtext, alert, loading }) => (
  <div className={`rounded-2xl border p-4 flex items-center gap-4 ${
    alert ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-gray-50'
  }`}>
    <div className={`p-2.5 rounded-xl ${alert ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
      {icon}
    </div>
    <div>
      <p className="text-[11px] uppercase tracking-[0.15em] text-gray-500 font-semibold">{label}</p>
      <p className={`text-xl font-bold ${alert ? 'text-amber-900' : 'text-gray-900'}`}>
        {loading ? <RubyGemMark size={20} drawing title="Loading" /> : value}
      </p>
      <p className="text-xs text-gray-500">{subtext}</p>
    </div>
  </div>
);

const DashboardView: React.FC<{
  metrics: QuickMetric[];
  revenue: RevenueSummary | null;
  revenueError: string | null;
  revenueLoading: boolean;
  leads: Lead[];
  tenants: Tenant[];
  onLeadClick: (lead: Lead) => void;
  onTenantClick: (tenant: Tenant) => void;
  onViewAllLeads: () => void;
  onViewAllTenants: () => void;
  formatDate: (ts?: number) => string;
  getStatusColor: (status: string) => string;
}> = ({ metrics, revenue, revenueError, revenueLoading, leads, tenants, onLeadClick, onTenantClick, onViewAllLeads, onViewAllTenants, formatDate, getStatusColor }) => {
  const colorMap = {
    amber: 'from-amber-400 to-orange-500',
    blue: 'from-blue-400 to-rose-500',
    green: 'from-emerald-400 to-green-500',
    purple: 'from-rose-400 to-pink-500',
    red: 'from-red-400 to-rose-500',
  };

  const iconBgMap = {
    amber: 'bg-amber-50 text-amber-700',
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    purple: 'bg-rose-50 text-rose-700',
    red: 'bg-red-50 text-red-700',
  };

  return (
    <div className="space-y-6">
      {/* Metrics Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {metrics.map((metric, idx) => (
          <div
            key={idx}
            className="relative overflow-hidden bg-gray-50 rounded-2xl border border-gray-200 p-5"
          >
            <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${colorMap[metric.color]}`} />
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-xl ${iconBgMap[metric.color]}`}>
                {metric.icon}
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.15em] text-gray-500 font-semibold">
                  {metric.label}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {metric.loading ? <RubyGemMark size={24} drawing title="Loading" /> : metric.value}
                </p>
                {metric.subtext && (
                  <p className="text-xs text-gray-500">{metric.subtext}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/*
        BILLING COVERAGE — what the money above is actually spread across.

        These three exist because the old panel counted TENANTS and called them
        subscriptions: a tenant with three locations and four rented devices
        counted as one, so nothing on the dashboard moved when a location or a
        device was added. Locations and devices are what the per-item structure
        actually bills.
      */}
      {revenueError && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-red-200 bg-red-50">
          <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
          <p className="text-sm text-red-700">
            Revenue could not be loaded — {revenueError}. The figures above are not current.
          </p>
        </div>
      )}
      {revenue?.partial && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-amber-200 bg-amber-50">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-800">
            {revenue.errors.length} tenant{revenue.errors.length === 1 ? '' : 's'} could not be read —
            these totals are a floor, not the whole platform.
          </p>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        <CoverageTile
          icon={<MapPin size={16} />}
          label="Billed locations"
          loading={revenueLoading && !revenue}
          value={revenueLoading && !revenue ? '—' : String(revenue?.counts.billedLocations ?? '—')}
          subtext={revenue ? `across ${revenue.counts.subscriptions} subscription${revenue.counts.subscriptions === 1 ? '' : 's'}` : 'carrying a software line'}
        />
        <CoverageTile
          icon={<Monitor size={16} />}
          label="Devices on subscription"
          loading={revenueLoading && !revenue}
          value={revenueLoading && !revenue ? '—' : String(revenue?.counts.deviceSubscriptions ?? '—')}
          subtext={revenue ? `${revenueMoney(revenue.billed.deviceCents)} / mo` : 'recurring hardware'}
        />
        <CoverageTile
          icon={<AlertTriangle size={16} />}
          label="Needs attention"
          loading={revenueLoading && !revenue}
          value={revenueLoading && !revenue
            ? '—'
            : String(
              (revenue?.needsAttention.activeWithoutPlan.length ?? 0)
              + (revenue?.needsAttention.divergentCount ?? 0)
              + (revenue?.needsAttention.unmodelledTenantIds?.length ?? 0),
            )}
          subtext={revenue
            ? `${revenue.needsAttention.activeWithoutPlan.length} unbilled · ${revenue.needsAttention.divergentCount} divergent · ${revenue.needsAttention.unmodelledTenantIds?.length ?? 0} unmatched`
            : 'unbilled, divergent or unmatched'}
          alert={!!revenue && (
            revenue.needsAttention.activeWithoutPlan.length > 0
            || revenue.needsAttention.divergentCount > 0
            || (revenue.needsAttention.unmodelledTenantIds?.length ?? 0) > 0
          )}
        />
      </div>

      {/*
        ENTITLEMENT vs BILLING.

        The tiles above read Stripe. This reads the other mismatch entirely:
        packages a tenant can USE that nothing bills for. Granting a package in
        Super Admin writes an entitlement and makes no billing call, so that
        gap is invisible to every revenue figure on this page.

        Reported, never auto-corrected — a nightly job must not start a charge
        on a real card.
      */}
      <BillingDriftPanel />

      {/* Two Column Layout */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Leads */}
        <div className="bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-500" />
              <h3 className="font-semibold text-gray-900">New Leads</h3>
            </div>
            <button
              onClick={onViewAllLeads}
              className="text-sm text-coffee-700 hover:text-coffee-900 hover:underline flex items-center gap-1"
            >
              View All <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="divide-y divide-gray-200">
            {leads.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Inbox className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No new leads</p>
              </div>
            ) : (
              leads.map((lead) => (
                <button
                  key={lead.id}
                  onClick={() => onLeadClick(lead)}
                  className="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-100 transition-colors text-left"
                >
                  <div>
                    <p className="font-medium text-gray-900">{lead.businessName}</p>
                    <p className="text-sm text-gray-500">{lead.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(lead.status || 'new')}`}>
                      {lead.status || 'new'}
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Active Tenants */}
        <div className="bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building className="w-5 h-5 text-amber-500" />
              <h3 className="font-semibold text-gray-900">Active Tenants</h3>
            </div>
            <button
              onClick={onViewAllTenants}
              className="text-sm text-coffee-700 hover:text-coffee-900 hover:underline flex items-center gap-1"
            >
              View All <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="divide-y divide-gray-200">
            {tenants.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Building className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No active tenants</p>
              </div>
            ) : (
              tenants.map((tenant) => (
                <button
                  key={tenant.id}
                  onClick={() => onTenantClick(tenant)}
                  className="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-100 transition-colors text-left"
                >
                  <div>
                    <p className="font-medium text-gray-900">{tenant.name || tenant.businessName}</p>
                    <p className="text-sm text-gray-500">{tenant.adminEmail}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(tenant.subscriptionStatus || 'inactive')}`}>
                        {formatStatus(tenant.subscriptionStatus || 'inactive')}
                      </span>
                      {tenant.cancelAtPeriodEnd && (
                        <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-red-100 text-red-700">
                          END
                        </span>
                      )}
                    </div>
                    <TenantRevenueCell tenant={tenant} revenue={revenue} />
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Leads View
const LeadsView: React.FC<{
  leads: Lead[];
  allLeadsCount: number;
  searchTerm: string;
  setSearchTerm: (s: string) => void;
  statusFilter: LeadStatus | 'all';
  setStatusFilter: (s: LeadStatus | 'all') => void;
  statusCounts: Record<string, number>;
  onLeadClick: (lead: Lead) => void;
  onOnboard: (lead: Lead) => void;
  formatDate: (ts?: number) => string;
  getStatusColor: (status: string) => string;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}> = ({ leads, allLeadsCount, searchTerm, setSearchTerm, statusFilter, setStatusFilter, statusCounts, onLeadClick, onOnboard, formatDate, getStatusColor, currentPage, totalPages, onPageChange }) => {
  const statuses: Array<{ id: LeadStatus | 'all'; label: string }> = [
    { id: 'all', label: 'All' },
    { id: 'new', label: 'New' },
    { id: 'contacted', label: 'Contacted' },
    { id: 'onboarded', label: 'Onboarded' },
    { id: 'closed', label: 'Closed' },
  ];

  return (
    <div className="space-y-4">
      {/* Search and Status Filters Row */}
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        {/* Search Box */}
        <div className="relative flex-shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search leads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2.5 w-full md:w-72 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-[#DC143C] focus:border-transparent"
          />
        </div>

        {/* Status Filters */}
        <div className="flex items-center gap-2 flex-wrap flex-1">
          {statuses.map((s) => (
            <button
              key={s.id}
              onClick={() => setStatusFilter(s.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === s.id
                  ? 'bg-coffee-800 text-white'
                  : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
              }`}
            >
              {s.label}
              <span className="ml-1.5 opacity-70">({statusCounts[s.id] || 0})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Business</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {leads.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                    <Inbox className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>No leads found</p>
                  </td>
                </tr>
              ) : (
                leads.map((lead) => (
                  <tr
                    key={lead.id}
                    onClick={() => onLeadClick(lead)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{lead.businessName}</p>
                      <p className="text-xs text-gray-500">{lead.businessType || 'Cafe'}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-900">{lead.name}</p>
                      <p className="text-xs text-gray-500">{lead.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${getStatusColor(lead.status || 'new')}`}>
                        {lead.status || 'new'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDate(lead.submittedAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {lead.status !== 'onboarded' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onOnboard(lead); }}
                          className="px-3 py-1.5 bg-green-100 text-green-700 text-xs font-medium rounded-lg hover:bg-green-200 transition-colors"
                        >
                          Convert
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-sm text-gray-500">
            Showing {leads.length} of {allLeadsCount} lead{allLeadsCount !== 1 ? 's' : ''}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="px-3 py-1.5 text-sm font-medium rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="px-3 py-1.5 text-sm font-medium rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Tenants View
const TenantsView: React.FC<{
  tenants: Tenant[];
  allTenantsCount: number;
  searchTerm: string;
  setSearchTerm: (s: string) => void;
  viewMode: 'grid' | 'list';
  setViewMode: (m: 'grid' | 'list') => void;
  onTenantClick: (tenant: Tenant) => void;
  onNavigateToProfile: (id: string) => void;
  formatDate: (ts?: number) => string;
  getStatusColor: (status: string) => string;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}> = ({ tenants, allTenantsCount, searchTerm, setSearchTerm, viewMode, setViewMode, onTenantClick, onNavigateToProfile, formatDate, getStatusColor, currentPage, totalPages, onPageChange }) => {
  return (
    <div className="space-y-4">
      {/* Search and View Toggle Row */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Search Box */}
        <div className="relative flex-shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search tenants..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2.5 w-full md:w-72 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-[#DC143C] focus:border-transparent"
          />
        </div>

        <div className="flex items-center gap-4">
          <p className="text-sm text-gray-500">
            {allTenantsCount} tenant{allTenantsCount !== 1 ? 's' : ''}
          </p>
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
            >
              <ListIcon className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
            >
              <Grid className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* List View */}
      {viewMode === 'list' && (
        <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Business</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Admin</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Plan</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {tenants.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                    <Building className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>No tenants found</p>
                  </td>
                </tr>
              ) : (
                tenants.map((tenant) => (
                  <tr
                    key={tenant.id}
                    onClick={() => onTenantClick(tenant)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{tenant.name || tenant.businessName}</p>
                      <p className="text-xs text-gray-500 font-mono">{tenant.id}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-900">{tenant.adminName || '—'}</p>
                      <p className="text-xs text-gray-500">{tenant.adminEmail}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${getStatusColor(tenant.subscriptionStatus || 'inactive')}`}>
                          {formatStatus(tenant.subscriptionStatus || 'inactive')}
                        </span>
                        {tenant.cancelAtPeriodEnd && (
                          <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-red-100 text-red-700 border border-red-200">
                            CANCELLING
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900">
                        ${((tenant.subscriptionCost || 0) / 100).toFixed(2)}/mo
                      </p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); onNavigateToProfile(tenant.id); }}
                        className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        View Profile
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Grid View */}
      {viewMode === 'grid' && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {tenants.length === 0 ? (
            <div className="col-span-full text-center py-12 text-gray-500">
              <Building className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No tenants found</p>
            </div>
          ) : (
            tenants.map((tenant) => (
              <div
                key={tenant.id}
                onClick={() => onTenantClick(tenant)}
                className="bg-gray-50 rounded-xl border border-gray-200 p-5 cursor-pointer hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Building className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${getStatusColor(tenant.subscriptionStatus || 'inactive')}`}>
                      {formatStatus(tenant.subscriptionStatus || 'inactive')}
                    </span>
                    {tenant.cancelAtPeriodEnd && (
                      <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-red-100 text-red-700 border border-red-200">
                        CANCELLING
                      </span>
                    )}
                  </div>
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{tenant.name || tenant.businessName}</h3>
                <p className="text-sm text-gray-500 mb-3">{tenant.adminEmail}</p>
                <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                  <span className="text-lg font-bold text-gray-900">
                    ${((tenant.subscriptionCost || 0) / 100).toFixed(2)}/mo
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); onNavigateToProfile(tenant.id); }}
                    className="text-sm text-coffee-700 hover:text-coffee-900 hover:underline"
                  >
                    View Profile →
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-sm text-gray-500">
            Showing {tenants.length} of {allTenantsCount} tenant{allTenantsCount !== 1 ? 's' : ''}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="px-3 py-1.5 text-sm font-medium rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="px-3 py-1.5 text-sm font-medium rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Lead Detail Panel
const LeadDetailPanel: React.FC<{
  lead: Lead;
  onUpdateStatus: (id: string, status: LeadStatus) => void;
  onDelete: (id: string) => void;
  onOnboard: () => void;
  formatDate: (ts?: number) => string;
  getStatusColor: (status: string) => string;
}> = ({ lead, onUpdateStatus, onDelete, onOnboard, formatDate, getStatusColor }) => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">{lead.businessName}</h2>
          <p className="text-gray-500">{lead.businessType || 'Cafe'}</p>
        </div>
        <span className={`px-3 py-1.5 text-sm font-medium rounded-full ${getStatusColor(lead.status || 'new')}`}>
          {lead.status || 'new'}
        </span>
      </div>

      {/* Contact Info */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="bg-gray-100 rounded-lg p-4">
          <p className="text-xs text-gray-500 mb-1">Contact Name</p>
          <p className="font-medium text-gray-900">{lead.name || '—'}</p>
        </div>
        <div className="bg-gray-100 rounded-lg p-4">
          <p className="text-xs text-gray-500 mb-1">Email</p>
          <p className="font-medium text-gray-900">{lead.email}</p>
        </div>
        <div className="bg-gray-100 rounded-lg p-4">
          <p className="text-xs text-gray-500 mb-1">Phone</p>
          <p className="font-medium text-gray-900">{lead.phone || '—'}</p>
        </div>
        <div className="bg-gray-100 rounded-lg p-4">
          <p className="text-xs text-gray-500 mb-1">Submitted</p>
          <p className="font-medium text-gray-900">{formatDate(lead.submittedAt)}</p>
        </div>
      </div>

      {/* Message */}
      {lead.message && (
        <div className="bg-gray-100 rounded-lg p-4">
          <p className="text-xs text-gray-500 mb-1">Message</p>
          <p className="text-gray-900">{lead.message}</p>
        </div>
      )}

      {/* Status Actions */}
      <div className="border-t border-gray-200 pt-4">
        <p className="text-sm font-medium text-gray-700 mb-3">Update Status</p>
        <div className="flex flex-wrap gap-2">
          {(['new', 'contacted', 'closed'] as LeadStatus[]).map((status) => (
            <button
              key={status}
              onClick={() => onUpdateStatus(lead.id!, status)}
              disabled={lead.status === status}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                lead.status === status
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 border-t border-gray-200 pt-4">
        {lead.status !== 'onboarded' && (
          <button
            onClick={onOnboard}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-coffee-800 text-white rounded-lg hover:bg-coffee-700 transition-colors font-medium"
          >
            <UserPlus className="w-5 h-5" />
            Send Deal
          </button>
        )}
        <button
          onClick={() => onDelete(lead.id!)}
          className="p-3 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default SuperAdminCommandCenter;
